import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { YStack, XStack, Text } from 'tamagui'
import type { Deck } from '../../content/decks/types'
import { SlideRenderer } from './SlideRenderer'

interface DeckViewerProps {
  deck: Deck
  workshopSlug: string
  viewId: string
  initialSlide?: number
  onClose: () => void
}

export function DeckViewer({
  deck,
  workshopSlug,
  viewId,
  initialSlide = 0,
  onClose,
}: DeckViewerProps) {
  const [current, setCurrent] = useState(initialSlide)
  const [direction, setDirection] = useState<'left' | 'right'>('right')
  const highWater = useRef(initialSlide)
  const startTime = useRef(Date.now())
  const total = deck.slides.length

  // Section tracking
  const sectionInfo = useMemo(() => {
    const dividerIndices = deck.slides
      .map((s, i) => (s.layout === 'sectionDivider' ? i : -1))
      .filter((i) => i >= 0)
    const totalSections = dividerIndices.length

    // Find which section the current slide belongs to
    let currentSection = 0
    for (let i = dividerIndices.length - 1; i >= 0; i--) {
      if (current >= dividerIndices[i]) {
        currentSection = i + 1
        break
      }
    }
    return { currentSection, totalSections }
  }, [deck.slides, current])

  // Update high-water mark
  useEffect(() => {
    if (current > highWater.current) {
      highWater.current = current
    }
  }, [current])

  // URL sync
  useEffect(() => {
    const path = `/workshops/${workshopSlug}/${deck.type}/${current}`
    history.replaceState(null, '', path)
  }, [current, workshopSlug, deck.type])

  // Send progress on unmount
  useEffect(() => {
    return () => {
      const data = JSON.stringify({
        viewId,
        slidesViewed: highWater.current + 1,
        totalSlides: total,
        durationMs: Date.now() - startTime.current,
      })
      navigator.sendBeacon('/api/deck/progress', data)
    }
  }, [viewId, total])

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= total) return
      setDirection(index > current ? 'right' : 'left')
      setCurrent(index)
    },
    [current, total],
  )

  const next = useCallback(() => goTo(current + 1), [goTo, current])
  const prev = useCallback(() => goTo(current - 1), [goTo, current])

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault()
          next()
          break
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'Backspace':
          e.preventDefault()
          prev()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [next, prev, onClose])

  // Click navigation
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('a')) return
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const x = e.clientX - rect.left
      if (x < rect.width / 3) {
        prev()
      } else {
        next()
      }
    },
    [next, prev],
  )

  // Inject transition styles
  useEffect(() => {
    const styleId = 'deck-slide-transitions'
    if (document.getElementById(styleId)) return
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(40px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideInLeft {
        from { transform: translateX(-40px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      .slide-enter-right { animation: slideInRight 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      .slide-enter-left { animation: slideInLeft 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    `
    document.head.appendChild(style)
    return () => {
      document.getElementById(styleId)?.remove()
    }
  }, [])

  const viewer = (
    <YStack
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      backgroundColor="$background"
      style={{ zIndex: 10001, cursor: 'pointer' }}
      onClick={handleClick}
    >
      {/* Slide content */}
      <YStack
        flex={1}
        key={current}
        className={direction === 'right' ? 'slide-enter-right' : 'slide-enter-left'}
      >
        <SlideRenderer slide={deck.slides[current]} />
      </YStack>

      {/* Footer */}
      <XStack
        data-is-slide-footer=""
        paddingHorizontal="$6"
        paddingVertical="$3"
        justifyContent="space-between"
        alignItems="center"
        borderTopWidth={1}
        borderTopColor="$border"
        style={{ cursor: 'default', zIndex: 10002 }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Text fontFamily="$mono" color="$textMuted" style={{ fontSize: '11px' }}>
          {sectionInfo.currentSection > 0
            ? `Section ${sectionInfo.currentSection} of ${sectionInfo.totalSections}`
            : deck.title}
        </Text>
        <Text
          fontFamily="$mono"
          color="$textMuted"
          display="none"
          $gtSm={{ display: 'flex' }}
          style={{ fontSize: '11px' }}
        >
          AI Slop Prevention Workshop | NYC | May 5, 2026
        </Text>
        <Text fontFamily="$mono" color="$textMuted" style={{ fontSize: '11px' }}>
          {current + 1} / {total}
        </Text>
        <Text
          fontFamily="$mono"
          color="$textMuted"
          display="none"
          $gtSm={{ display: 'flex' }}
          style={{ fontSize: '11px' }}
        >
          {'<->'} navigate {'  ·  '} esc close
        </Text>
      </XStack>
    </YStack>
  )

  return createPortal(viewer, document.body)
}
